
"use client"

import type React from "react"
import axios from "axios"
import { useState, useEffect } from "react"
import { Button } from "../../ui/button"
import { Input } from "../../ui/input"
import { Textarea } from "../../ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select"
import { Label } from "../../ui/label"
import { CalendarIcon, Save, X } from "lucide-react"
import { Calendar } from "../../ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover"
import { format, isWeekend, eachDayOfInterval } from "date-fns"
import { cn } from "../../lib/utils"
import { useToast } from "../../hooks/use-toast"
import { Loader2 } from "lucide-react"

export interface LeaveRecordWithDetails {
  requestID: number
  leaveType: string
  startDate: Date
  endDate: Date
  reason: string
  leaveDayDetails: LeaveDayDetail[]
}

interface AddLeaveFormProps {
  leaveToEdit?: LeaveRecordWithDetails
  defaultValues?: {
    requestID?: number
    leaveType: string
    startDate: Date
    endDate: Date
    reason: string
    leaveDayDetails: LeaveDayDetail[]
  }
  onSubmitSuccess?: () => void
}

interface LeaveDayDetail {
  date: string // YYYY-MM-DD format
  type: "Full Day" | "First-Half" | "Second-Half"
}
interface LeaveType {
  leaveTypeId: number
  leaveName: string
  remainingLeaves: number | null
}

export function AddLeaveForm({
  defaultValues,
  leaveToEdit,
  onSubmitSuccess,
}: AddLeaveFormProps) {
  const [leaveType, setLeaveType] = useState(defaultValues?.leaveType || "")
  const [startDate, setStartDate] = useState<Date | undefined>(defaultValues?.startDate)
  const [endDate, setEndDate] = useState<Date | undefined>(defaultValues?.endDate)
  const [reason, setReason] = useState(defaultValues?.reason || "")
  const [leaveDayDetails, setLeaveDayDetails] = useState<LeaveDayDetail[]>(defaultValues?.leaveDayDetails || [])
  const [loading, setLoading] = useState(false)
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([])
  const { toast } = useToast()
  const [loadingLeaves, setLoadingLeaves] = useState(true)
  const [notification, setNotification] = useState<{
  message: string
  type: "error" | "success" | "info"
} | null>(null)
const [isStartPickerOpen, setIsStartPickerOpen] = useState(false)
const [isEndPickerOpen, setIsEndPickerOpen] = useState(false)
// new state to track "no policy" condition
const [noPolicyAssigned, setNoPolicyAssigned] = useState(false);


  const fetchLeaveTypes = async (force = false) => {
  if (!force && leaveTypes.length > 0) return;

  try {
    setLoadingLeaves(true);
    const res = await axios.get(
      process.env.REACT_APP_API_URL + "/api/Leave/GetUserRemainingLeaves",
      { withCredentials: true }
    );

    if (res.data?.result) {
      const list = res.data.result;

      // ✅ If API returns "No Policy Assigned"
      if (
        list.length === 1 &&
        list[0].leaveTypeId === null &&
        list[0].leaveName === "No Policy Assigned"
      ) {
        setLeaveTypes([]);  // CLEAR THE DROPDOWN
        setNotification({
          message: list[0].leaveName, // 🟢 SHOW EXACT MESSAGE FROM DB
          type: "error",
        });
        setNoPolicyAssigned(true);
        return; // IMPORTANT!
      }
      setLeaveTypes(list);
    } else {
      setLeaveTypes([]);
    }
  } catch (err) {
    console.error("Fetch leave types failed:", err);
    toast({
      title: "Error",
      description: "Failed to fetch leave balances.",
      variant: "destructive",
    });
    setLeaveTypes([]);
    setNoPolicyAssigned(false);
  } finally {
    setLoadingLeaves(false);
  }
};
useEffect(() => {
  fetchLeaveTypes()
}, []) // only once on mount

  const getLeaveTextClass = (name: string) => {
  if (name.includes("Casual")) return "text-yellow-600 bg-yellow-100"
  if (name.includes("Sick")) return "text-red-600 bg-red-100"
  if (name.includes("Leave Without Pay")) return "text-orange-600 bg-orange-100"
  if (name.includes("Work From Home")) return "text-blue-600 bg-blue-100"
  if (name.includes("Comp-Off")) return "text-green-600 bg-green-100"
  return "text-gray-900"
}

const getLeavePillClass = (name: string) => {
  if (name.includes("Casual")) return "bg-yellow-50 text-yellow-700"
  if (name.includes("Sick")) return "bg-red-50 text-red-700"
  if (name.includes("Leave Without Pay")) return "bg-orange-50 text-orange-700"
  if (name.includes("Work From Home")) return "bg-blue-50 text-blue-700"
  if (name.includes("Comp-Off")) return "bg-green-50 text-green-700"
  return "bg-gray-50 text-gray-800"
}


  const selectedTextClass = leaveType ? getLeaveTextClass(leaveType) : "text-gray-900"
  

  // Function to disable weekends
  const disableWeekends = (date: Date) => isWeekend(date)

  // Automatically update end date to match start date when start date changes
  useEffect(() => {
    if (startDate) {
      // Normalize start date
      const normalizedStartDate = new Date(startDate)
      normalizedStartDate.setHours(0, 0, 0, 0)
      
      // Normalize current end date for comparison
      const normalizedEndDate = endDate ? new Date(endDate) : null
      if (normalizedEndDate) {
        normalizedEndDate.setHours(0, 0, 0, 0)
      }
      
      // Update end date to match start date if they're different
      // This ensures end date always matches start date when start date is changed
      if (!normalizedEndDate || normalizedStartDate.getTime() !== normalizedEndDate.getTime()) {
        setEndDate(normalizedStartDate)
      }
    } else if (!startDate && endDate) {
      // If start date is cleared, also clear end date
      setEndDate(undefined)
    }
  }, [startDate]) // Only depend on startDate - endDate is checked but not in deps to avoid loops

  // Generate leave day details when start or end date changes
  useEffect(() => {
    if (startDate && endDate && startDate <= endDate) {
      const days = eachDayOfInterval({ start: startDate, end: endDate })
      const newDetails: LeaveDayDetail[] = days
        .filter((day) => !isWeekend(day)) // Filter out weekends
        .map((day) => ({
          date: format(day, "yyyy-MM-dd"),
          type: "Full Day",
        }))
      setLeaveDayDetails(newDetails)
    } else {
      setLeaveDayDetails([])
    }
  }, [startDate, endDate])

  const handleDayDetailChange = (date: string, type: "Full Day" | "First-Half" | "Second-Half") => {
    setLeaveDayDetails((prev) => prev.map((detail) => (detail.date === date ? { ...detail, type } : detail)))
  }

  // 🔹 Converts frontend leave type name to backend leaveTypeID
  const mapLeaveTypeToId = (leaveType: string): number => {
    const map: Record<string, number> = {
      "Casual Leave": 1,
      "Sick Leave": 2,
      "Leave Without Pay": 3,
      "Work From Home": 4,
      "Comp-Off": 5,
    }

    return map[leaveType] || 0
  }


    const handleSubmit = async () => {
  if (!leaveType || !startDate || !endDate || !reason || leaveDayDetails.length === 0) {
  setNotification({
    message: "Please fill all required fields, including reason.",
    type: "error",
  });
  return;
}



  // Helper: format Date to YYYY-MM-DD (local date, no timezone)
  const formatDateToYMD = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const selectedLeaveType = leaveTypes.find((t) => t.leaveName === leaveType);
  if (!selectedLeaveType) {
    setNotification({
      message: "Please select a valid leave type.",
      type: "error",
    });
    return;
  }

  const payload = {
    requestID: defaultValues?.requestID || 0,
    userID: 123, // Replace with actual logged-in user ID
    leaveTypeID: selectedLeaveType.leaveTypeId,
    leaveStartDate: formatDateToYMD(startDate), // ✅ local date only
    leaveEndDate: formatDateToYMD(endDate),     // ✅ local date only
    reason,
    comments: undefined,
    cancelledByUserID: 0,
    cancelledOn: null,
    cancelReason: null,
    modUserId: 123,
    leaveDayDetails: leaveDayDetails.map((d) => ({
      leaveDate: d.date, // Already in YYYY-MM-DD from your state
      dayType: d.type,
    })),
  };

  try {
    setLoading(true);
    const res = await fetch(
      process.env.REACT_APP_API_URL +"/api/Leave/InsertOrUpdateLeaveRequest",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok || data?.status === -1) {
      const errorMsg = data?.message || data?.error || "Something went wrong while submitting your leave request.";
      toast({
        title: "Error",
        description: errorMsg,
        duration: 4000,
        className: "border border-red-300",
      });
      return;
    }

    const isEdit = defaultValues?.requestID && defaultValues.requestID > 0;
    toast({
      title: "Success",
      description: isEdit ? "Leave updated successfully!" : (data?.message || "Leave Request submitted successfully."),
      duration: 3000,
      className: "border border-green-300",
    });

    if (onSubmitSuccess) {
  onSubmitSuccess(); // parent can close modal & refresh leave history
}

    // Re-fetch leave balances
    fetchLeaveTypes(true);

    // Reset form
    setLeaveType("");
    setStartDate(undefined);
    setEndDate(undefined);
    setReason("");
    setLeaveDayDetails([]);
  } catch (error: any) {
    console.error("Error submitting leave request:", error);
    toast({
      title: "Error",
      description: error.message || "Failed to submit leave request.",
      duration: 4000,
      className: "border border-red-300",
    });
  } finally {
    setLoading(false);
  }
};



  const isFormEmpty = () => {
    return (
      !leaveType &&
      !startDate &&
      !endDate &&
      !reason &&
      leaveDayDetails.length === 0
    )
  }

  const handleCancel = () => {
    setLeaveType("")
    setStartDate(undefined)
    setEndDate(undefined)
    setReason("")
    setLeaveDayDetails([])
    toast({
      title: "Cancelled",
      description: "Leave request form cleared.",
      duration: 2000,
    })
  }


  // Disable all past dates
const disablePastDates = (date: Date) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date < today // disables any date before today
}

// Combined function to disable weekends and past dates
const disableInvalidDates = (date: Date) => disableWeekends(date) || disablePastDates(date)

  return (
    <>
      <div className="space-y-6 px-3 sm:px-6">
        {notification && (
          <div
            className={`
              px-4 py-3 rounded-md mb-4 text-sm font-medium flex items-center justify-between
              ${notification.type === "error" ? "bg-red-50 text-red-700" : ""}
              ${notification.type === "success" ? "bg-green-50 text-green-700" : ""}
              ${notification.type === "info" ? "bg-blue-50 text-blue-700" : ""}
            `}
          >
            <span>{notification.message}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setNotification(null)}
              className="ml-2 p-0"
            >
              ✕
            </Button>
          </div>
        )}
          <h2 className="text-xl font-semibold text-gray-900">
          </h2>


          <div className="space-y-4">
            {/* Leave Type Dropdown */}
          <div className="space-y-2">
            <Label htmlFor="leaveType" className="text-sm font-medium text-gray-700">
              Leave Type
            </Label>
            <Select value={leaveType} onValueChange={setLeaveType} disabled={noPolicyAssigned}>
              <SelectTrigger
                className={cn(
                  "bg-white border-gray-200",
                  !leaveType ? "text-gray-500" : selectedTextClass,
                )}
              >
                <SelectValue placeholder="Select Leave Type" />
              </SelectTrigger>

              <SelectContent className="bg-white border border-gray-200 p-2">
                <div className="space-y-3">
                  {leaveTypes.map((type : LeaveType) => (
                    <SelectItem
                      key={type.leaveTypeId}
                      value={type.leaveName}
                      className={cn(
                        "flex items-center justify-between px-2 py-2 rounded-md cursor-pointer gap-2",
                        getLeavePillClass(type.leaveName) || "bg-gray-50 text-gray-800",
                      )}
                    >
                      <span>{type.leaveName}</span>
                      <span className="ml-2 text-xs font-semibold rounded-full bg-white/40 px-2 py-0.5">
                        {type.remainingLeaves === null ? "" : `${type.remainingLeaves} left`}
                      </span>
                    </SelectItem>
                  ))}
                </div>
              </SelectContent>
            </Select>
          </div>

   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="startDate" className="text-sm font-medium text-gray-700 w-full">
              Start Date
            </Label>
            <Popover open={isStartPickerOpen} onOpenChange={(open) => {
                if (noPolicyAssigned) return; // block opening
                setIsStartPickerOpen(open);
              }}>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full justify-start text-left font-normal bg-white border-gray-200 text-gray-900",
                    !startDate && "text-gray-500",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "dd-MM-yyyy") : "dd-mm-yyyy"}
                </Button>
              </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={8}
                  className="w-auto p-4 bg-white border border-gray-200 rounded-2xl shadow-2xl animate-in fade-in-80">
                  
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => {
                        if (!date) return
                        // Normalize date (set time to 0 for consistent comparison)
                        const normalizedDate = new Date(date)
                        normalizedDate.setHours(0, 0, 0, 0)
                        
                        setStartDate(normalizedDate)
                        // Always update end date to match start date when start date changes
                        setEndDate(normalizedDate)
                        setIsStartPickerOpen(false)
                        setTimeout(() => setIsEndPickerOpen(true), 150) // ✅ auto-open end picker
                      }}
                      disabled={disableInvalidDates}
                      className={cn(
                        "rounded-xl text-sm font-medium shadow-inner bg-gray-50 text-gray-900 p-3",
                        "[&_.rdp-head_cell]:text-gray-500",
                        "[&_.rdp-day]:h-9 [&_.rdp-day]:w-9 [&_.rdp-day]:rounded-full [&_.rdp-day]:transition-all [&_.rdp-day]:duration-150",
                        "[&_.rdp-day:hover]:bg-gray-200",
                        "[&_.rdp-day_selected]:bg-green-600 [&_.rdp-day_selected]:text-white [&_.rdp-day_selected:hover]:bg-green-700",
                        "[&_.rdp-day_today]:border [&_.rdp-day_today]:border-green-600 [&_.rdp-day_today]:font-bold",
                        "[&_.rdp-day_disabled]:text-gray-400 [&_.rdp-day_disabled]:opacity-40 [&_.rdp-day_disabled]:line-through"
                      )}
                      initialFocus
                    />
                </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="endDate" className="text-sm font-medium text-gray-700 w-full">
              End Date
            </Label>
            <Popover open={isEndPickerOpen} onOpenChange={(open) => {
              if (noPolicyAssigned) return;
              setIsEndPickerOpen(open);
            }}>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal bg-white border-gray-200 text-gray-900",
                  !endDate && "text-gray-500",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "dd-MM-yyyy") : "dd-mm-yyyy"}
              </Button>
            </PopoverTrigger>

            <PopoverContent
              align="start"
              sideOffset={8}
              className="w-auto bg-white border border-gray-200 rounded-2xl shadow-2xl animate-in fade-in-80"
            >
              <Calendar
                mode="single"
                selected={endDate || startDate} // ✅ select start date if end date not chosen yet
                defaultMonth={startDate}        // ✅ open same month as start date
                onSelect={(date) => {
                  if (!date) return

                  // Normalize the selected date
                  const normalizedDate = new Date(date)
                  normalizedDate.setHours(0, 0, 0, 0)
                  
                  // Normalize start date for comparison
                  const normalizedStartDate = startDate ? new Date(startDate) : null
                  if (normalizedStartDate) {
                    normalizedStartDate.setHours(0, 0, 0, 0)
                  }

                  if (normalizedStartDate && normalizedDate < normalizedStartDate) {
                    setNotification({ message: "End date cannot be before start date.", type: "error" })
                    return
                  }

                  setEndDate(normalizedDate)
                  setIsEndPickerOpen(false)
                }}
                disabled={(date) => {
                  const today = new Date()
                  today.setHours(0, 0, 0, 0)
                  
                  // Normalize the date being checked
                  const normalizedDate = new Date(date)
                  normalizedDate.setHours(0, 0, 0, 0)
                  
                  // Normalize startDate for comparison
                  const normalizedStartDate = startDate ? new Date(startDate) : null
                  if (normalizedStartDate) {
                    normalizedStartDate.setHours(0, 0, 0, 0)
                  }

                  return (
                    isWeekend(normalizedDate) ||
                    normalizedDate < today ||
                    (normalizedStartDate ? normalizedDate < normalizedStartDate : false)
                  )
                }}
                className={cn(
                  "rounded-xl text-sm font-medium shadow-inner bg-gray-50 text-gray-900 p-3",
                  "[&_.rdp-head_cell]:text-gray-500",
                  "[&_.rdp-day]:h-9 [&_.rdp-day]:w-9 [&_.rdp-day]:rounded-full [&_.rdp-day]:transition-all [&_.rdp-day]:duration-150",
                  "[&_.rdp-day:hover]:bg-gray-200",
                  "[&_.rdp-day_selected]:bg-green-600 [&_.rdp-day_selected]:text-white [&_.rdp-day_selected:hover]:bg-green-700",
                  "[&_.rdp-day_today]:border [&_.rdp-day_today]:border-green-600 [&_.rdp-day_today]:font-bold",
                  "[&_.rdp-day_disabled]:text-gray-400 [&_.rdp-day_disabled]:opacity-40 [&_.rdp-day_disabled]:line-through"
                )}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          </div>
        </div>

        {leaveType === "Sick Leave" && (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700 w-full">Attachments</Label>

            {/* Keep chooser visible until 5 files are attached */}
            
          </div>
        )}

        {leaveDayDetails.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-base font-medium text-gray-900">Leave Day Details</h3>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {leaveDayDetails.map((dayDetail) => (
                <div key={dayDetail.date} className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    {format(new Date(dayDetail.date), "yyyy-MM-dd")}
                  </Label>
                  <Select
                    value={dayDetail.type}
                    onValueChange={(value: "Full Day" | "First-Half" | "Second-Half") =>
                      handleDayDetailChange(dayDetail.date, value)
                    }
                  >
                    <SelectTrigger
                      className={cn(
                        "bg-white border-gray-200 w-full",
                        !leaveType ? "text-gray-500" : selectedTextClass,
                      )}
                    >
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200">
                      <SelectItem
                        value="Full Day"
                        className="text-gray-900 hover:bg-gray-50"
                      >
                        Full Day
                      </SelectItem>
                      <SelectItem
                        value="First-Half"
                        className="text-gray-900 hover:bg-gray-50"
                      >
                        First-Half
                      </SelectItem>
                      <SelectItem
                        value="Second-Half"
                        className="text-gray-900 hover:bg-gray-50"
                      >
                        Second-Half
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="reason" className="text-sm font-medium text-gray-700">
              Reason*
            </Label>
            <span className="text-xs text-gray-500">
              {reason.length}/1000
            </span>
          </div>
          <Textarea
            id="reason"
            placeholder="Justify your reason for leave"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={noPolicyAssigned}
            maxLength={1000}
            className={cn(
              "w-full min-h-[100px] bg-white border-gray-200 text-gray-900 focus:ring-blue-500 focus:border-blue-500",
              noPolicyAssigned && "cursor-not-allowed opacity-70"
            )}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 w-full sm:w-auto">
        {!defaultValues?.requestID && (
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading || noPolicyAssigned || isFormEmpty()}
            className="flex items-center gap-2 text-gray-700 hover:bg-gray-100 bg-transparent"
          >
            <X className="h-4 w-4" />
            Clear Form
          </Button>
        )}
        <Button onClick={handleSubmit} 
        disabled={loading || noPolicyAssigned}
        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white">
          <Save className="h-4 w-4" />
          Submit
        </Button>
      </div>

    </div>
    {/* Full-Screen Loader Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 flex flex-col items-center gap-3 shadow-lg">
            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
            <p className="text-gray-700 text-sm">
              Submitting your leave request...
            </p>
          </div>
        </div>
      )}

      {/* Full-Screen Page Loader for fetching leave types */}
    {loadingLeaves && (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-40">
        <div className="bg-white rounded-xl p-6 flex flex-col items-center gap-3 shadow-lg">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-gray-700 text-sm">
            Loading leave balances...
          </p>
        </div>
      </div>
    )}
    </>
  )
}
